import type { Editor } from '@tiptap/react';
import { DOMParser as PMDOMParser, Slice } from '@tiptap/pm/model';
import { Selection, TextSelection } from '@tiptap/pm/state';

import { replaceSelectionWithSlice } from '@/utils/editor-utils/editor-utils';

import type { AgentEditRange } from '../../edit-target';
import { ensureReplaceSelectionSession } from '../replace-selection-sessions';

type ReplaceSelectionInput = {
  content?: unknown;
};

type ReplaceSelectionExecutionResult = {
  output: unknown;
  status: 'applied' | 'no-op' | 'missing-selection' | 'failed';
};

const getReplaceSelectionContent = (input: unknown): string | null => {
  if (!input || typeof input !== 'object') {
    return null;
  }

  return typeof (input as ReplaceSelectionInput).content === 'string'
    ? ((input as ReplaceSelectionInput).content as string)
    : null;
};

const clampRangeToDoc = (editor: Editor, range: { from: number; to: number }): { from: number; to: number } => {
  const maxPos = editor.state.doc.content.size;
  const from = Math.max(0, Math.min(range.from, maxPos));
  const to = Math.max(from, Math.min(range.to, maxPos));
  return { from, to };
};

// The inline-vs-block decision comes from the session's frozen `selectionKind`, chosen from the
// original endpoints when the session was created. Reading the live remapped range here would let a
// mid-stream remap flip the replacement path, so the frozen kind is the single source of truth.
const normalizeFragmentWrapper = (content: string, selectionKind: 'inline' | 'block'): HTMLElement => {
  const wrapper = document.createElement('div');
  wrapper.innerHTML = content.trim();

  if (selectionKind === 'inline') {
    const onlyChild = wrapper.childNodes.length === 1 ? wrapper.firstChild : null;
    if (onlyChild instanceof HTMLElement && onlyChild.tagName === 'P') {
      wrapper.innerHTML = onlyChild.innerHTML;
    }
  }

  return wrapper;
};

const parseReplacementSlice = (editor: Editor, content: string, selectionKind: 'inline' | 'block'): Slice => {
  const wrapper = normalizeFragmentWrapper(content, selectionKind);
  return PMDOMParser.fromSchema(editor.state.schema).parseSlice(wrapper);
};

const applyReplacementSlice = ({
  editor,
  range,
  slice,
  selectionKind,
}: {
  editor: Editor;
  range: { from: number; to: number };
  slice: Slice;
  selectionKind: 'inline' | 'block';
}): { nextRange: { from: number; to: number } } => {
  const clampedRange = clampRangeToDoc(editor, range);

  if (selectionKind === 'block') {
    const tr = editor.state.tr;
    tr.replaceRange(clampedRange.from, clampedRange.to, slice);

    const nextFrom = tr.mapping.map(clampedRange.from, -1);
    const nextTo = Math.max(nextFrom, tr.mapping.map(clampedRange.to, 1));

    try {
      const resolvedPos = tr.doc.resolve(Math.max(0, Math.min(nextTo, tr.doc.content.size)));
      tr.setSelection(Selection.near(resolvedPos, -1));
    } catch {
      // Selection.near may fail transiently on malformed streamed content; replacement still succeeded.
    }

    tr.scrollIntoView();
    editor.view.dispatch(tr);

    return {
      nextRange: {
        from: nextFrom,
        to: nextTo,
      },
    };
  }

  const tr = editor.state.tr.setSelection(TextSelection.create(editor.state.doc, clampedRange.from, clampedRange.to));
  replaceSelectionWithSlice(tr, slice, false);
  const nextRange = {
    from: tr.selection.from,
    to: tr.selection.to,
  };
  editor.view.dispatch(tr);
  return { nextRange };
};

export function executeReplaceSelectionTool({
  requestId,
  toolCallId,
  editor,
  input,
  fallbackRange,
}: {
  requestId: string;
  toolCallId: string;
  editor: Editor;
  input: unknown;
  fallbackRange?: AgentEditRange;
}): ReplaceSelectionExecutionResult {
  const session = ensureReplaceSelectionSession({
    requestId,
    toolCallId,
    editor,
    fallbackRange,
  });
  const content = getReplaceSelectionContent(input);

  if (session == null) {
    return {
      status: 'missing-selection',
      output: { success: false, applied: false, reason: 'Targeted selection is missing.' },
    };
  }

  if (content == null) {
    return {
      status: 'failed',
      output: { success: false, applied: false, reason: 'replaceSelection.content must be a string.' },
    };
  }

  if (session.lastAppliedContent === content) {
    return {
      status: 'no-op',
      output: { success: true, applied: false, noop: true },
    };
  }

  try {
    const slice = parseReplacementSlice(editor, content, session.selectionKind);
    const { nextRange } = applyReplacementSlice({
      editor,
      range: session.currentRange,
      slice,
      selectionKind: session.selectionKind,
    });

    session.currentRange = nextRange;
    session.lastAppliedContent = content;

    return {
      status: 'applied',
      output: {
        success: true,
        applied: true,
      },
    };
  } catch (error) {
    const reason = error instanceof Error ? error.message : 'Failed to apply replaceSelection';

    return {
      status: 'failed',
      output: { success: false, applied: false, reason },
    };
  }
}
